<script lang="ts">
  import { QUAD, TERMINAL_ROLE, departedIn, roleSegments } from './quadrant';

  // A contributor-role breakdown bar + legend, from a {role: count} map. Shared by
  // the activity heatmap, the cohort heatmap, and the weekly-activity trend tooltip.
  // `total` sets the bar's denominator (e.g. a cohort size); `percent` shows % in
  // the legend instead of raw counts.
  //
  // The bar itself is the LIVE role mix. Members who have since left carry the
  // terminal `departed` role (from the month they went), and they get a figure after
  // the legend's divider instead of a slice — one bucket that big inside the bar
  // would flatten the mix the bar exists to show. The track behind the bar is what
  // makes the arithmetic visible: with a `total` that counts them, the bar
  // deliberately under-fills, and the gap is the people who left.
  let {
    roles,
    total = undefined,
    label = 'By contributor role',
    percent = false,
  }: {
    roles: Record<string, number> | null | undefined;
    total?: number | undefined;
    label?: string;
    percent?: boolean;
  } = $props();

  const segs = $derived(roleSegments(roles, total));
  const gone = $derived(departedIn(roles));
  const meta = QUAD[TERMINAL_ROLE];
  const fmt = (s: (typeof segs)[number]) =>
    percent ? `${Math.round(s.pct * 100)}%` : s.count.toLocaleString();
  // Share of the same denominator the segments use — the caller's `total` when it has
  // one, otherwise the whole map (live + departed), so the figures still add up.
  const goneFmt = $derived.by(() => {
    if (!percent) return gone.toLocaleString();
    const denom = total ?? segs.reduce((sum, s) => sum + s.count, 0) + gone;
    return `${Math.round((gone / (denom || 1)) * 100)}%`;
  });
</script>

{#if segs.length || gone > 0}
  <div class="eyebrow mb-1 text-[0.55rem] text-base-content/62">{label}</div>
  <div class="flex h-2 overflow-hidden rounded-full bg-base-200">
    {#each segs as s (s.role)}
      <div style:width="{s.pct * 100}%" style:background={s.color} title="{s.label} {fmt(s)}"></div>
    {/each}
  </div>
  <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[0.55rem]">
    {#each segs as s (s.role)}
      <span class="inline-flex items-center gap-1" style:color={s.color}>
        <span class="inline-block h-1.5 w-1.5 rounded-full" style:background={s.color}></span>{s.label} {fmt(s)}
      </span>
    {/each}
    {#if gone > 0}
      <span
        class="inline-flex items-center gap-1 {segs.length ? 'border-l hair pl-2' : ''}"
        style:color={meta.color}
        title="Counted here, but no longer members — not part of the mix above"
      >
        <span class="inline-block h-1.5 w-1.5 rounded-full" style:background={meta.color}></span>{meta.label} {goneFmt}
      </span>
    {/if}
  </div>
{/if}
